// Habits (docs/63): a repeating to-do that keeps score. The rules of its streak, as the kit's HabitRules
// (Habits.swift) has them, and the habit's to-dos, as the kit's Store makes them ("Habits (docs/63)" in Store.swift).
// The kit is the reference; cases/habits.json checks that both agree (test/core/habits.test.ts, SharedCasesHabitsTests).
//
// A habit is a record of its own; each day it is due it puts one ordinary to-do into Today, whose completion counts for
// that day. A step of the streak is a scheduled day, or a week or month of a flexible habit (N times a week or month).
// Never Miss Twice keeps one missed step: it bridges the streak and adds nothing; two in a row end it.

import { addDays, at, dayOf, daysBetween, dayParts, minDay, startOfDay, timeOf, weekday, weekStart } from "./days";
import { makeTodo, type Habit, type HabitEarlierSchedule, type HabitSchedule, type Todo } from "./records";
import type { Day, RepeatRule, Start, Weekday } from "./types";
import { activeMonthDays } from "./repeats";

export type HabitUnit = "day" | "week" | "month";

/** One step: a scheduled day, or a week or month of a flexible habit, with its state. */
export interface HabitStep {
  first: Day;
  last: Day;
  /** done, kept (missed, the streak goes on), missed, open (today's or this week's, not done yet). */
  state: "done" | "kept" | "missed" | "open";
  done: number;
  target: number;
  /** What it is a step of: a day, a week or a month. */
  unit: HabitUnit;
  /** The first step after How Often changed to another unit: the streak starts again here. */
  startsAnew?: boolean;
}

/** A habit's numbers at one moment (HabitProgress). */
export interface HabitProgress {
  streak: number;
  best: number;
  /** The streak is a personal best that shows (isBestOf): the badge turns gold. */
  isBest: boolean;
  unit: HabitUnit;
  isDueToday: boolean;
  isDoneToday: boolean;
  /** The last step that ended was missed and kept: "missed yesterday". */
  missedLastStep: boolean;
  periodDone: number;
  periodTarget: number;
}

const KNOWN = new Set(["everyNDays", "weekdays", "timesPerWeek", "timesPerMonth", "monthDays"]);

/** What one step is (HabitSchedule.unit). */
export function unitOf(schedule: HabitSchedule): HabitUnit {
  return schedule.kind === "timesPerWeek" ? "week" : schedule.kind === "timesPerMonth" ? "month" : "day";
}

export function isFlexible(schedule: HabitSchedule): boolean {
  return unitOf(schedule) !== "day";
}

/** Times per week or month; 1 for a habit on set days. */
export function targetOf(schedule: HabitSchedule): number {
  return isFlexible(schedule) ? Math.max(schedule.count ?? 1, 1) : 1;
}

export function keepsOneMiss(habit: Pick<Habit, "neverMissTwice">): boolean {
  return habit.neverMissTwice ?? true;
}

function daysInMonth(day: Day): number {
  const { year, month } = dayParts(day);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function monthStart(day: Day): Day {
  return `${day.slice(0, 8)}01` as Day;
}

function nextMonthStart(day: Day): Day {
  return addDays(monthStart(day), daysInMonth(day));
}

/** Whether `day` is one of its set days, counted from `start` (HabitSchedule.isScheduled). Flexible habits have none. */
export function isScheduled(schedule: HabitSchedule, day: Day, start: Day): boolean {
  if (day < start) return false;
  switch (schedule.kind) {
    case "everyNDays": return daysBetween(start, day) % Math.max(schedule.interval ?? 1, 1) === 0;
    case "weekdays": return (schedule.weekdays ?? []).includes(weekday(day));
    case "monthDays": {
      const number = dayParts(day).date;
      const length = daysInMonth(day);
      return (schedule.monthDays ?? []).some((wanted) => wanted === number || (wanted > length && number === length));
    }
    default: return false;
  }
}

/**
 * The next set day after today, for a habit on set weekdays ("Next: Sat" on its card in Habits; the Habits page E2,
 * 2026-10-29); undefined for any other How Often, and for days in a break. New on the web: the kit has no such rule yet.
 */
export function nextPlannedDay(habit: Habit, today: Day, zone: string): Day | undefined {
  if (habit.schedule.kind !== "weekdays" || (habit.schedule.weekdays ?? []).length === 0) return undefined;
  const start = scheduleStart(habit, zone);
  for (let offset = 1; offset <= 14; offset++) {
    const day = addDays(today, offset);
    if (isScheduled(habit.schedule, day, start) && !isPaused(habit, day)) return day;
  }
  return undefined;
}

/** The day its count starts: `startsOn`, else the day it was made (Habit.firstDay). */
export function habitStart(habit: Habit, zone: string): Day {
  return habit.startsOn ?? dayOf(habit.createdAt, zone);
}

/** The day the current How Often began (Habit.scheduleStart): the day after the last earlier rule, or its first day. */
export function scheduleStart(habit: Habit, zone: string): Day {
  const until = habit.earlierSchedules?.at(-1)?.until;
  return until !== undefined ? addDays(until, 1) : habitStart(habit, zone);
}

/** Whether two How Often are the same, as the kit's HabitSchedule compares them (the days as sets). */
export function sameSchedule(a: HabitSchedule, b: HabitSchedule): boolean {
  const sameSet = (x?: readonly number[], y?: readonly number[]) =>
    x === undefined || y === undefined ? x === y : new Set(x).size === new Set(y).size && x.every((n) => y.includes(n));
  return a.kind === b.kind && a.interval === b.interval && a.count === b.count && sameSet(a.weekdays, b.weekdays) && sameSet(a.monthDays, b.monthDays);
}

/**
 * How Often changes today (Habit.changeSchedule; Bastian, 2026-10-27): the rule it had keeps the past. The new one
 * counts from today, or from this week or month when it counts weeks or months; what ran before holds until the day
 * before. A change back to the rule just before it closes the gap, so changing and changing back leaves the history as
 * it was.
 */
export function changeSchedule(habit: Habit, next: HabitSchedule, today: Day, zone: string, firstWeekday: Weekday = 2): Habit {
  if (sameSchedule(next, habit.schedule)) return habit;
  const unit = unitOf(next);
  const begins = unit === "day" ? today : unit === "week" ? weekStart(today, firstWeekday) : monthStart(today);
  const lastDay = addDays(begins, -1);
  const earlier = habit.earlierSchedules ?? [];
  const reaches = (one: HabitEarlierSchedule) => (one.until ?? "0000-01-01") >= lastDay;
  // The rule that held on the day before the new one begins: an earlier one that reaches it, else the old one.
  const ruleThen = earlier.find(reaches)?.schedule ?? habit.schedule;
  const kept = earlier.filter((one) => !reaches(one));
  const before = kept.at(-1);
  const from = before?.until !== undefined ? addDays(before.until, 1) : habitStart(habit, zone);
  if (from <= lastDay) kept.push({ schedule: ruleThen, until: lastDay });
  if (kept.length > 0 && sameSchedule(kept.at(-1)!.schedule, next)) kept.pop();
  const changed: Habit = { ...habit, schedule: next };
  if (kept.length > 0) changed.earlierSchedules = kept; else delete changed.earlierSchedules;
  return changed;
}

export function isPaused(habit: Habit, day: Day): boolean {
  return (habit.pauses ?? []).some((pause) => day >= (pause.from ?? "0000-01-01") && day <= (pause.until ?? "9999-12-31"));
}

/** The break that holds today or begins later, if any. */
export function currentPause(habit: Habit, today: Day): { from?: Day; until?: Day } | undefined {
  return (habit.pauses ?? []).find((pause) => (pause.until ?? "9999-12-31") >= today && (pause.from ?? "0000-01-01") <= today);
}

/**
 * The steps from its first day through today, each with its state (HabitRules.steps). Each stretch is judged by the How
 * Often it had then (`earlierSchedules`); the first step after a change to another unit starts the streak again.
 */
export function habitSteps(habit: Habit, doneDays: ReadonlySet<Day>, today: Day, zone: string, firstWeekday: Weekday = 2): HabitStep[] {
  let from = habitStart(habit, zone);
  if (from > today) return [];
  // The current rule's stretch has no end: its week or month runs on past today.
  const stretches: { schedule: HabitSchedule; from: Day; until?: Day }[] = [];
  for (const earlier of habit.earlierSchedules ?? []) {
    if (earlier.until === undefined) continue;
    if (earlier.until >= from) stretches.push({ schedule: earlier.schedule, from, until: earlier.until });
    const after = addDays(earlier.until, 1);
    if (after > from) from = after;
  }
  if (from <= today) stretches.push({ schedule: habit.schedule, from });
  const steps: HabitStep[] = [];
  let previousUnit: HabitUnit | undefined;
  for (const stretch of stretches) {
    const own = stretchSteps(habit, stretch.schedule, stretch.from, stretch.until, doneDays, today, firstWeekday);
    const unit = unitOf(stretch.schedule);
    if (previousUnit !== undefined && previousUnit !== unit && own.length > 0) own[0] = { ...own[0]!, startsAnew: true };
    previousUnit = unit;
    steps.push(...own);
  }
  return keepingOneMiss(steps, keepsOneMiss(habit));
}

/** The steps of one stretch under one How Often, from `start` through `end` (open: the current rule) and today. */
function stretchSteps(
  habit: Habit, schedule: HabitSchedule, start: Day, end: Day | undefined, doneDays: ReadonlySet<Day>, today: Day, firstWeekday: Weekday,
): HabitStep[] {
  const steps: HabitStep[] = [];
  const through = end === undefined ? today : minDay(end, today);
  const unit = unitOf(schedule);
  if (unit === "day") {
    if (!KNOWN.has(schedule.kind)) return [];
    for (let day = start; day <= through; day = addDays(day, 1)) {
      if (!isScheduled(schedule, day, start) || isPaused(habit, day)) continue;
      const done = doneDays.has(day);
      steps.push({ first: day, last: day, state: done ? "done" : day === today ? "open" : "missed", done: done ? 1 : 0, target: 1, unit });
    }
  } else {
    let period = unit === "week" ? weekStart(start, firstWeekday) : monthStart(start);
    while (period <= through) {
      const next = unit === "week" ? addDays(period, 7) : nextMonthStart(period);
      // A week or month cut short by a change ends with its rule's last day.
      const last = end === undefined ? addDays(next, -1) : minDay(addDays(next, -1), end);
      let available = 0;
      let done = 0;
      for (let day = period > start ? period : start; day <= last; day = addDays(day, 1)) {
        if (isPaused(habit, day)) continue;
        available += 1;
        if (doneDays.has(day)) done += 1;
      }
      if (available > 0) {
        const target = Math.min(targetOf(schedule), available);
        const isCurrent = last >= today;
        steps.push({ first: period > start ? period : start, last, state: done >= target ? "done" : isCurrent ? "open" : "missed", done, target, unit });
      }
      period = next;
    }
  }
  return steps;
}

/** Marks the misses Never Miss Twice keeps: a miss with no miss right before or after it. */
export function keepingOneMiss(steps: readonly HabitStep[], rule: boolean): HabitStep[] {
  if (!rule) return [...steps];
  return steps.map((step, index) => {
    if (step.state !== "missed") return step;
    const before = index > 0 ? steps[index - 1]!.state : "done";
    const after = index + 1 < steps.length ? steps[index + 1]!.state : "open";
    return before !== "missed" && after !== "missed" ? { ...step, state: "kept" } : step;
  });
}

/**
 * Steps done in a row, counted back from the newest; open ones are passed over, a kept miss bridges. It goes back no
 * further than a change of How Often to another unit.
 */
export function streakOf(steps: readonly HabitStep[]): number {
  let count = 0;
  for (let index = steps.length - 1; index >= 0; index--) {
    const step = steps[index]!;
    if (step.state === "done") count += 1;
    else if (step.state === "missed") return count;
    if (step.startsAnew) return count;
  }
  return count;
}

/** The longest streak ever, by the same rule, in the unit of the newest step (days and weeks don't add up). */
export function bestOf(steps: readonly HabitStep[]): number {
  const unit = steps.at(-1)?.unit;
  let best = 0;
  let run = 0;
  for (const step of steps) {
    if (step.startsAnew) run = 0;
    if (step.unit !== unit) continue;
    if (step.state === "done") { run += 1; best = Math.max(best, run); }
    else if (step.state === "missed") run = 0;
  }
  return best;
}

/** From what length a record shows (HabitRules.bestFrom): 14 days, 4 weeks or 3 months. */
export function bestFrom(unit: HabitUnit): number {
  return unit === "day" ? 14 : unit === "week" ? 4 : 3;
}

/** Whether the current streak is a personal best that shows (HabitRules.isBest, rules A and B): an earlier streak
 *  existed, the current one has passed it, and it is at least bestFrom long. */
export function isBestOf(steps: readonly HabitStep[], unit: HabitUnit): boolean {
  let lastMiss = -1;
  steps.forEach((step, index) => { if (step.state === "missed" || step.startsAnew) lastMiss = index; });
  if (lastMiss < 0) return false;
  // Since a change to another unit, the streak is the first in its unit, as a habit's first streak is.
  if (steps[lastMiss]!.startsAnew) return false;
  const earlier = bestOf(steps.slice(0, lastMiss + 1));
  const current = streakOf(steps);
  return earlier > 0 && current > earlier && current >= bestFrom(unit);
}

/** Whether it wants a to-do today (HabitRules.isDue). Every N days counts from the day the current rule began. */
export function isDue(habit: Habit, today: Day, doneDays: ReadonlySet<Day>, zone: string, firstWeekday: Weekday = 2): boolean {
  if (habit.archivedAt !== undefined || habitStart(habit, zone) > today || isPaused(habit, today)) return false;
  const unit = unitOf(habit.schedule);
  if (unit === "day") return isScheduled(habit.schedule, today, scheduleStart(habit, zone));
  if (habit.restsOn === today) return false;
  const first = unit === "week" ? weekStart(today, firstWeekday) : monthStart(today);
  let before = 0;
  for (const day of doneDays) if (day >= first && day < today) before += 1;
  return before < targetOf(habit.schedule);
}

/**
 * Whether Not Today may take it off today (HabitRules.canRestToday; docs/63 15d): only a flexible habit, due and not
 * done today, and only while the days after today in its week or month, without breaks, can still reach what is
 * missing. When today is needed, there is none. Checked by `notToday` in cases/habits.json.
 */
export function canRestToday(habit: Habit, doneDays: ReadonlySet<Day>, today: Day, zone: string, firstWeekday: Weekday = 2): boolean {
  if (!isFlexible(habit.schedule) || doneDays.has(today) || !isDue(habit, today, doneDays, zone, firstWeekday)) return false;
  const current = habitSteps(habit, doneDays, today, zone, firstWeekday).at(-1);
  if (!current || current.first > today || current.last < today) return false;
  let later = 0;
  for (let day = addDays(today, 1); day <= current.last; day = addDays(day, 1)) if (!isPaused(habit, day)) later += 1;
  return later >= current.target - current.done;
}

/**
 * Whether it is off today with Not Today, and Back to Today is offered (HabitRules.restsToday; docs/63 15d, Bastian
 * 2026-10-30): a flexible habit whose restsOn is today, not done today, that would be due today without the rest (not
 * paused, not archived, its count not reached). Checked by `restsToday` in the `notToday` cases of cases/habits.json.
 */
export function restsToday(habit: Habit, doneDays: ReadonlySet<Day>, today: Day, zone: string, firstWeekday: Weekday = 2): boolean {
  if (!isFlexible(habit.schedule) || habit.restsOn !== today || doneDays.has(today)) return false;
  const awake: Habit = { ...habit };
  delete awake.restsOn;
  return isDue(awake, today, doneDays, zone, firstWeekday);
}

/** Its numbers today (HabitRules.progress). */
export function habitProgress(habit: Habit, doneDays: ReadonlySet<Day>, today: Day, zone: string, firstWeekday: Weekday = 2): HabitProgress {
  const steps = habitSteps(habit, doneDays, today, zone, firstWeekday);
  const isDoneToday = doneDays.has(today);
  const ended = [...steps].reverse().find((step) => step.last < today);
  const last = steps.at(-1);
  const current = last && last.last >= today ? last : undefined;
  return {
    streak: streakOf(steps),
    best: bestOf(steps),
    isBest: isBestOf(steps, unitOf(habit.schedule)),
    unit: unitOf(habit.schedule),
    isDueToday: isDue(habit, today, doneDays, zone, firstWeekday) || isDoneToday,
    isDoneToday,
    missedLastStep: ended?.state === "kept",
    periodDone: current?.done ?? (isDoneToday ? 1 : 0),
    periodTarget: current?.target ?? 1,
  };
}

/** The milestones that get a card (docs/63 section 9). */
export const MILESTONES: Readonly<Record<HabitUnit, readonly number[]>> = {
  day: [7, 30, 100, 365], week: [4, 12, 26, 52], month: [3, 6, 12],
};

export function newMilestone(streak: number, unit: HabitUnit, shown: readonly number[]): number | undefined {
  return MILESTONES[unit].includes(streak) && !shown.includes(streak) ? streak : undefined;
}

/** What a tick of a flexible habit's day did to its week or month (HabitPeriodTick). */
export interface HabitPeriodTick {
  unit: "week" | "month";
  /** Done in it after the tick, and the number planned (fewer when the habit began within it). */
  done: number;
  target: number;
  /** This tick reached the number: the week (month) is done. */
  closes: boolean;
  /** The streak after the tick, in weeks or months. */
  streak: number;
}

/** A tick the morning after a missed day (docs/70): today's to-do that was ticked, and yesterday, the day it may belong to. */
export interface LateHabitTick {
  todoID: string;
  habitID: string;
  title: string;
  day: Day;
}

/**
 * The day a tick the morning after may belong to (HabitRules.lateDay; docs/70, Bastian 2026-11-02): yesterday, when it
 * was one of the habit's planned days (by the How Often it had then, after it began, not in a break) and isn't done;
 * undefined otherwise, and always for a habit counted by the week or month. Never Miss Twice doesn't matter. Checked
 * by `lateDay` in cases/habits.json.
 */
export function lateDay(habit: Habit, doneDays: ReadonlySet<Day>, today: Day, zone: string, firstWeekday: Weekday = 2): Day | undefined {
  if (habit.archivedAt !== undefined) return undefined;
  const yesterday = addDays(today, -1);
  const step = habitSteps(habit, doneDays, today, zone, firstWeekday).filter((one) => one.unit === "day" && one.first === yesterday).pop();
  return step && (step.state === "missed" || step.state === "kept") ? yesterday : undefined;
}

/**
 * What a tick of a flexible habit's day did to its week or month (HabitRules.periodTick; the toast and Habits' capsule,
 * design/habits/celebrations boards 9–11, Bastian 2026-10-31). `doneDays` are the days done after the tick, `day` the
 * one ticked. Undefined for a habit on set days, or when `day` is not done, falls in a break, or lies outside the
 * current week or month: the toast then says "Completed". `periodTicks` in cases/habits.json.
 */
export function periodTick(habit: Habit, doneDays: ReadonlySet<Day>, day: Day, today: Day, zone: string, firstWeekday: Weekday = 2): HabitPeriodTick | undefined {
  if (!isFlexible(habit.schedule) || !doneDays.has(day) || isPaused(habit, day)) return undefined;
  const steps = habitSteps(habit, doneDays, today, zone, firstWeekday);
  const current = steps.at(-1);
  if (!current || current.unit === "day" || current.first > day || day > current.last || current.last < today) return undefined;
  return { unit: current.unit, done: current.done, target: current.target, closes: current.done === current.target, streak: streakOf(steps) };
}

/** Habits' capsule (board 11): a flexible habit whose week (month) is done, for the rest of it. */
export function periodDone(progress: HabitProgress): "week" | "month" | undefined {
  if (progress.unit === "day" || progress.periodTarget <= 0 || progress.periodDone < progress.periodTarget) return undefined;
  return progress.unit;
}

/** How long a milestone is, in words, for its moment's line (the kit's `MilestoneMoment.line`). */
export type MilestoneSpan = "week" | "month" | "overThreeMonths" | "season" | "halfYear" | "year";

export function milestoneSpan(milestone: number, unit: HabitUnit): MilestoneSpan | undefined {
  const spans: Record<HabitUnit, Record<number, MilestoneSpan>> = {
    day: { 7: "week", 30: "month", 100: "overThreeMonths", 365: "year" },
    week: { 4: "month", 12: "season", 26: "halfYear", 52: "year" },
    month: { 3: "season", 6: "halfYear", 12: "year" },
  };
  return spans[unit][milestone];
}

/**
 * The id of a habit's to-do for a day, the same on every device (HabitRules.todoID): the habit's id with its version
 * nibble set to 8 and its last four bytes mixed with the day's number since 1 January 1970.
 */
export function habitTodoID(habitID: string, day: Day): string {
  const hex = habitID.replace(/-/g, "").toLowerCase();
  const bytes = Array.from({ length: 16 }, (_, index) => parseInt(hex.slice(index * 2, index * 2 + 2), 16));
  const { year, month, date } = dayParts(day);
  const number = (Math.floor(Date.UTC(year, month - 1, date) / 86_400_000) >>> 0);
  bytes[6] = (bytes[6]! & 0x0f) | 0x80;
  bytes[12]! ^= (number >>> 24) & 0xff;
  bytes[13]! ^= (number >>> 16) & 0xff;
  bytes[14]! ^= (number >>> 8) & 0xff;
  bytes[15]! ^= number & 0xff;
  const text = bytes.map((byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
  return `${text.slice(0, 8)}-${text.slice(8, 12)}-${text.slice(12, 16)}-${text.slice(16, 20)}-${text.slice(20)}`;
}

/** Whether `id` is one of a habit's day to-dos (habitTodoID of any day): the habit's id with its version nibble set
 *  to 8; the last four bytes, which carry the day, aside (HabitRules.isDayTodoID). */
export function isDayTodoID(id: string, habitID: string): boolean {
  const own = id.replace(/-/g, "").toUpperCase();
  const habit = habitID.replace(/-/g, "").toUpperCase();
  if (own.length !== 32 || habit.length !== 32) return false;
  const version = ((parseInt(habit.slice(12, 14), 16) & 0x0f) | 0x80).toString(16).toUpperCase().padStart(2, "0");
  return own.slice(0, 24) === habit.slice(0, 12) + version + habit.slice(14, 24);
}

/**
 * The habit a to-do was before Make a To-Do (docs/63 15e; Bastian, 2026-10-29), which the flame on it brings back with
 * its history instead of making a new one (HabitRules.formerHabit). `ids` are the to-do's and those of the occurrences
 * it came from (spawnedFrom). Among the stopped habits, one that became one of them (becameTodoID) or whose day one of
 * them was (habitTodoID); the one stopped last when there are more. `revive.match` in cases/habits.json.
 */
export function formerHabit<H extends Pick<Habit, "id" | "archivedAt" | "becameTodoID">>(ids: readonly string[], habits: readonly H[]): H | undefined {
  const known = new Set(ids.map((id) => id.toUpperCase()));
  let found: H | undefined;
  for (const habit of habits) {
    if (habit.archivedAt === undefined) continue;
    const fits = (habit.becameTodoID !== undefined && known.has(habit.becameTodoID.toUpperCase())) || ids.some((id) => isDayTodoID(id, habit.id));
    if (fits && (found === undefined || habit.archivedAt.getTime() > (found.archivedAt?.getTime() ?? -Infinity))) found = habit;
  }
  return found;
}

// MARK: - The badge (HabitBadge)

/** What the badge at the row's end shows: strong while today asks for it, soft once done or not due; the number only. */
export interface HabitBadge {
  /** The streak, or undefined for the flame alone. */
  number?: number;
  isStrong: boolean;
  /** A personal best: the whole chip gold (option 2 of 2026-10-25, on trial; the trophy is the fallback). */
  isBest: boolean;
  /** The line under the title: "2 of 3 this week" or "missed yesterday". */
  detail?: { kind: "week" | "month"; done: number; target: number } | { kind: "missed" };
}

export function habitBadge(progress: HabitProgress, showsStreaks = true): HabitBadge {
  const badge: HabitBadge = { isStrong: progress.isDueToday && !progress.isDoneToday, isBest: showsStreaks && progress.isBest };
  if (showsStreaks && progress.streak > 0) badge.number = progress.streak;
  if (progress.unit !== "day") badge.detail = { kind: progress.unit, done: progress.periodDone, target: progress.periodTarget };
  else if (progress.missedLastStep) badge.detail = { kind: "missed" };
  return badge;
}

/**
 * The habit's status beside a row's other details (HabitBadge.status(isDone:); the display decisions of 2026-10-29,
 * point 2 C): the week's or month's count stays once the day is done ("3 of 3 this week"), "missed yesterday" goes,
 * since the tick has said enough.
 */
export function habitStatus(badge: HabitBadge, isDone: boolean): HabitBadge["detail"] {
  return isDone && badge.detail?.kind === "missed" ? undefined : badge.detail;
}

// MARK: - A habit's to-dos (Store.swift)

/** The day a habit's to-do stands for: its start day, or the day it was completed. */
export function habitDayOf(todo: Pick<Todo, "start" | "completedAt">, zone: string): Day | undefined {
  if (todo.start.kind === "on") return todo.start.day;
  return todo.completedAt ? dayOf(todo.completedAt, zone) : undefined;
}

/** The days a habit was done: the days of its completed to-dos. */
export function doneDaysOf(habitID: string, todos: readonly Todo[], zone: string): Set<Day> {
  const days = new Set<Day>();
  for (const todo of todos) {
    if (todo.habitID !== habitID || todo.completedAt === undefined) continue;
    const day = habitDayOf(todo, zone);
    if (day) days.add(day);
  }
  return days;
}

/**
 * The to-dos a stopped habit was ticked as while it was a plain to-do (docs/63 15e; Bastian, 2026-10-29: "making a to-do
 * a habit merely shows the history and counts it, but it is always kept"; HabitRules.ticksWhileOff). Brought back, the
 * habit takes them as its own days (they get its habitID), so the days off it was ticked on count as done; the days off
 * without a tick stay missed. They are the completed plain to-dos among the one it became with Make a To-Do
 * (becameTodoID, or, for one stopped before that field, one of its day's ids) and the copies its repeat made since
 * (spawnedFrom, at any depth), each counting for its day (habitDayOf). Left out: a day the habit already has (one day,
 * one record), a day still to come, and of two ticks of one day all but the one ticked first. `revive.ticks` in
 * cases/habits.json.
 */
export function ticksWhileOff(habit: Pick<Habit, "id" | "becameTodoID">, todos: readonly Todo[], today: Day, zone: string): string[] {
  const key = (id: string) => id.toUpperCase();
  const became = habit.becameTodoID === undefined ? undefined : key(habit.becameTodoID);
  const reached = new Set<string>();
  for (const todo of todos) {
    if (todo.habitID === undefined && (key(todo.id) === became || isDayTodoID(todo.id, habit.id))) reached.add(key(todo.id));
  }
  if (reached.size === 0) return [];
  // The copies made from them, and those made from the copies, as far as they go.
  const children = new Map<string, string[]>();
  for (const todo of todos) {
    if (todo.habitID !== undefined || todo.spawnedFrom === undefined) continue;
    const from = key(todo.spawnedFrom);
    children.set(from, [...(children.get(from) ?? []), key(todo.id)]);
  }
  const queue = [...reached];
  for (let id = queue.pop(); id !== undefined; id = queue.pop()) {
    for (const child of children.get(id) ?? []) {
      if (reached.has(child)) continue;
      reached.add(child);
      queue.push(child);
    }
  }
  const taken = doneDaysOf(habit.id, todos, zone);
  const ticked = todos.filter((todo) => reached.has(key(todo.id)) && todo.habitID === undefined && todo.completedAt !== undefined)
    .sort((a, b) => (a.completedAt!.getTime() - b.completedAt!.getTime()) || (key(a.id) < key(b.id) ? -1 : key(a.id) > key(b.id) ? 1 : 0));
  const result: string[] = [];
  for (const todo of ticked) {
    const day = habitDayOf(todo, zone);
    if (day === undefined || day > today || taken.has(day)) continue;
    taken.add(day);
    result.push(todo.id);
  }
  return result;
}

/** The habits that run (not archived), in the user's order. */
export function sortedHabits(habits: readonly Habit[]): Habit[] {
  return habits.filter((habit) => habit.archivedAt === undefined).sort((a, b) => a.order - b.order);
}

/**
 * The order of Habits' lists (Today, All, an area's habits; Bastian, 2026-10-29): the open ones first, then the ones
 * done today, each in the order given (the user's). One just ticked counts as open while it lingers, so it keeps its
 * place for the moment of ticking, as a to-do does in a list (the kit's HabitRules.doneLast; `listOrder` in
 * cases/habits.json).
 */
export function habitsDoneLast<Item>(items: readonly Item[], isDone: (item: Item) => boolean): Item[] {
  return [...items.filter((item) => !isDone(item)), ...items.filter(isDone)];
}

/** A habit's to-do for a day (Store.dayTodo): the same record on every device. */
export function dayTodo(habit: Habit, day: Day, zone: string, completedAt?: Date): Todo {
  const todo = makeTodo({
    id: habitTodoID(habit.id, day), title: habit.title, notes: habit.notes, start: { kind: "on", day },
    order: habit.order - 1_000_000, createdAt: startOfDay(day, zone), leftInboxAt: startOfDay(day, zone), habitID: habit.id,
  });
  if (habit.areaID !== undefined) todo.areaID = habit.areaID;
  if (completedAt) todo.completedAt = completedAt;
  if (habit.reminder) {
    const [hour, minute] = timeOf(habit.reminder, habit.reminderZone ?? zone).split(":").map(Number);
    todo.reminder = at(day, hour ?? 9, minute ?? 0, zone);
    if (habit.reminderZone !== undefined) todo.reminderZone = habit.reminderZone;
    // The habit's time is the day's series' time (docs/71), so This One can differ from it.
    todo.seriesReminder = todo.reminder;
    if (habit.reminderZone !== undefined) todo.seriesReminderZone = habit.reminderZone;
  }
  return todo;
}

/**
 * The habits' to-dos brought up to date (Store.refreshHabitDays): each habit due today has its to-do (made once, the
 * same id on every device), one no longer due today loses its open one, open ones of earlier days go (a day not done is
 * missed), two of one day become one, and an open one follows its habit's title, notes, area and reminder. Returns what
 * to save and delete; nothing when all is as it should be.
 */
export function refreshHabitDays(habits: readonly Habit[], todos: readonly Todo[], today: Day, zone: string, firstWeekday: Weekday = 2):
  { saved: Todo[]; deleted: string[] } {
  const saved: Todo[] = [];
  const deleted = new Set<string>();
  if (habits.length === 0 && !todos.some((todo) => todo.habitID !== undefined)) return { saved, deleted: [] };
  const known = new Set(habits.map((habit) => habit.id));
  for (const todo of todos) {
    if (todo.habitID === undefined || !known.has(todo.habitID) || todo.completedAt !== undefined) continue;
    const day = habitDayOf(todo, zone);
    if (day !== undefined && day < today) deleted.add(todo.id);
  }
  for (const habit of habits) {
    const ofToday = todos.filter((todo) => todo.habitID === habit.id && !deleted.has(todo.id) && habitDayOf(todo, zone) === today);
    let todo = ofToday[0];
    if (ofToday.length > 1) {
      todo = ofToday.find((other) => other.completedAt !== undefined) ?? [...ofToday].sort((a, b) => (a.id < b.id ? -1 : 1))[0];
      for (const other of ofToday) if (other.id !== todo!.id) deleted.add(other.id);
    }
    const due = isDue(habit, today, doneDaysOf(habit.id, todos, zone), zone, firstWeekday);
    if (todo) {
      if (todo.completedAt !== undefined) continue;
      if (!due) { deleted.add(todo.id); continue; }
      const fresh = dayTodo(habit, today, zone);
      const next: Todo = { ...todo, title: habit.title, notes: habit.notes };
      if (habit.areaID !== undefined) next.areaID = habit.areaID; else delete next.areaID;
      // Its series' time is the habit's; its own follows only while it was the series' (an older day without a
      // series' time always did): one set for this day alone (This One) stays (docs/71).
      const follows = todo.seriesReminder === undefined || todo.reminder?.getTime() === todo.seriesReminder.getTime();
      if (fresh.seriesReminder) next.seriesReminder = fresh.seriesReminder; else delete next.seriesReminder;
      if (fresh.seriesReminderZone !== undefined) next.seriesReminderZone = fresh.seriesReminderZone; else delete next.seriesReminderZone;
      if (follows) {
        if (fresh.reminder) next.reminder = fresh.reminder; else delete next.reminder;
        if (fresh.reminderZone !== undefined) next.reminderZone = fresh.reminderZone; else delete next.reminderZone;
      }
      const same = next.title === todo.title && next.notes === todo.notes && next.areaID === todo.areaID
        && next.reminder?.getTime() === todo.reminder?.getTime() && next.reminderZone === todo.reminderZone
        && next.seriesReminder?.getTime() === todo.seriesReminder?.getTime() && next.seriesReminderZone === todo.seriesReminderZone;
      if (!same) saved.push(next);
    } else if (due) {
      saved.push(dayTodo(habit, today, zone));
    }
  }
  return { saved, deleted: [...deleted] };
}

/** How Often from a to-do's repeat, when the flame makes it a habit; undefined for what a habit doesn't do. */
export function scheduleFromRepeat(rule: RepeatRule | undefined, start: Start): HabitSchedule | undefined {
  if (!rule || rule.afterCompletion) return undefined;
  switch (rule.unit) {
    case "day": return { kind: "everyNDays", interval: Math.max(rule.interval, 1) };
    case "week": {
      if (rule.interval !== 1) return undefined;
      if (rule.weekdays && rule.weekdays.length > 0) {
        return rule.weekdays.length === 7 ? { kind: "everyNDays", interval: 1 } : { kind: "weekdays", weekdays: [...rule.weekdays] };
      }
      return start.kind === "on" ? { kind: "weekdays", weekdays: [weekday(start.day)] } : undefined;
    }
    case "month":
      if (rule.interval !== 1 || rule.monthPosition) return undefined;
      if (activeMonthDays(rule)) return { kind: "monthDays", monthDays: [...(activeMonthDays(rule) ?? [])] };
      if (start.kind !== "on") return undefined;
      return { kind: "monthDays", monthDays: [dayParts(start.day).date] };
    default: return undefined;
  }
}

/** The repeat a habit's to-do keeps when it turns back into a plain to-do; undefined for N times a week or month. */
export function repeatOfSchedule(schedule: HabitSchedule): RepeatRule | undefined {
  switch (schedule.kind) {
    case "everyNDays": return { interval: Math.max(schedule.interval ?? 1, 1), unit: "day", afterCompletion: false };
    case "weekdays": return { interval: 1, unit: "week", afterCompletion: false, weekdays: [...(schedule.weekdays ?? [])] as Weekday[] };
    case "monthDays": return (schedule.monthDays ?? []).length > 0
      ? { interval: 1, unit: "month", afterCompletion: false, monthDays: [...(schedule.monthDays ?? [])].sort((a, b) => a - b) } : undefined;
    default: return undefined;
  }
}
